import { useCallback, useEffect, useState } from "react";
import { getAttendanceDetailsByType, getDailyAttendanceSummary, getEmployeesForAttendance } from "../../../services/AppService";
import MessageModal from "../../../components/AdminComponents/Modals/MessageModal";
import PageFade from "../../../components/Login/PageFade";
import closeButton from "../../../assets/images/close.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";


const DailyAttendance = () => {
    const { t } = useTranslation();
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [summary, setSummary] = useState([]);
    const [selectedDeptEmployees, setSelectedDeptEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailTitle, setDetailTitle] = useState("Employee Details");
    const navigate = useNavigate();


    const [detailType, setDetailType] = useState("DEFAULT"); 

    // 2. Logic to return the dynamic translation based on current detailType
    const getDetailTitle = () => {
        switch (detailType) {
            case 'PRESENT': return t('attendance.allPresentEmployees');
            case 'NOT_PRESENT': return t('attendance.allAbsentEmployees');
            default: return t('attendance.employeeDetails');
        }
    };


    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "error",
        title: "",
        message: "",
    });

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Data Fetch Error",
            message: msg,
        });
    };

    const fetchSummary = useCallback(async () => {
        if (!date) return;
        try {
            setLoading(true);
            const data = await getDailyAttendanceSummary(date);
            // Null handling: Ensure summary is always an array
            setSummary(Array.isArray(data) ? data : []);
            setSelectedDeptEmployees([]); // Reset details when date changes
        } catch (error) {
            console.error("Failed to fetch summary:", error);
            showError("Could not load the attendance summary for the selected date.");
            setSummary([]);
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const handleDeptClick = async (deptId) => {
        if (!deptId || !date) return;
        try {
            setDetailLoading(true);
            setDetailType("DEFAULT");
            const data = await getEmployeesForAttendance(deptId, date);
            // Null handling: Ensure employees list is always an array
            setSelectedDeptEmployees(Array.isArray(data) ? data : []);

            // Scroll to details for better UX
            setTimeout(() => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
        } catch (error) {
            console.error("Failed to fetch department details:", error);
            showError("Could not load employee details for this department.");
            setSelectedDeptEmployees([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleTotalClick = async (type) => {
        try {
            setDetailLoading(true);
           setDetailType(type);
            const data = await getAttendanceDetailsByType(date, type);
            setSelectedDeptEmployees(Array.isArray(data) ? data : []);

            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        } catch (error) {
            showError("Failed to fetch categorized details.");
        } finally {
            setDetailLoading(false);
        }

    }

    const calculateTotal = (key) => {
        if (!summary || summary.length === 0) return 0;
        return summary.reduce((acc, curr) => acc + (Number(curr?.[key]) || 0), 0);
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h4 className="text-xl font-bold text-[#2c5282] tracking-tight">{t('attendance.dailyAttendance')}</h4>
                    <img
                        src={closeButton}
                        className="w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity"
                        alt="Close"
                        onClick={() => navigate(-1)}
                    />


                </div>

                <div className="mb-8 flex items-center gap-4 bg-slate-50 p-4 rounded-lg border border-slate-200 w-fit">
                    <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                        {t('attendance.todaysDate')}: {new Date().toLocaleDateString()}
                    </span>
                    <label className="font-bold text-gray-700 text-sm">{t('attendance.viewRecordsFor')} :</label>
                    <input
                        type="date"
                        value={date}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setDate(e.target.value)}
                        className="border-2 border-slate-200 p-1.5 rounded-lg focus:border-blue-400 outline-none font-semibold text-gray-700"
                    />
                </div>

                {/* Main Summary Table */}
                <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-sm bg-white">
                    <table className="w-full text-center border-collapse md:text-base text-sm">
                        <thead className="bg-[#a0adf1] text-white uppercase tracking-wider">
                            <tr>
                                <th className="p-3 border-r border-blue-300 w-12">{t('tables.srNo')}.</th>
                                <th className="p-3 border-r border-blue-300 text-left">{t('attendance.department')}</th>
                                <th className="p-3 border-r border-blue-300 bg-green-600">{t('attendance.totalStaff')}</th>
                                <th className="p-3 border-r border-blue-300">{t('attendance.onTime')}</th>
                                <th className="p-3 border-r border-blue-300">{t('attendance.lateMark')}</th>
                                <th className="p-3 border-r border-blue-300">{t('attendance.onDuty')}</th>
                                <th className="p-3 border-r border-blue-300 bg-blue-600">{t('attendance.totalPresent')}</th>
                                <th className="p-3 border-r border-blue-300">{t('attendance.leave')}</th>
                                <th className="p-3 border-r border-blue-300">{t('attendance.absent')}</th>
                                <th className="p-3 bg-red-600">{t('attendance.totalAbsent')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                            {loading ? (
                                <tr><td colSpan={10} className="p-10 text-slate-400 italic">Fetching summary data...</td></tr>
                            ) : summary.length === 0 ? (
                                <tr><td colSpan={10} className="p-10 text-slate-400 italic">No attendance records found for this date.</td></tr>
                            ) : (
                                summary.map((item, idx) => (
                                    <tr key={item.departmentId || idx} className="hover:bg-blue-50/50 transition-colors">
                                        <td className="p-2 border-r border-slate-100 font-bold text-gray-400">{idx + 1}</td>
                                        <td
                                            className="p-2 border-r border-slate-100 text-left text-blue-700 underline cursor-pointer font-bold hover:text-blue-900"
                                            onClick={() => handleDeptClick(item.departmentId)}
                                        >
                                            {item.departmentName || "Unknown Department"}
                                        </td>
                                        <td className="p-2 border-r border-slate-100 bg-green-50 font-bold text-green-800">{item.totalStaff || 0}</td>
                                        <td className="p-2 border-r border-slate-100">{item.onTime || 0}</td>
                                        <td className="p-2 border-r border-slate-100">{item.lateMark || 0}</td>
                                        <td className="p-2 border-r border-slate-100">{item.onDuty || 0}</td>
                                        <td className="p-2 border-r border-slate-100 bg-blue-50 font-bold text-blue-800">{item.totalPresent || 0}</td>
                                        <td className="p-2 border-r border-slate-100">{item.leave || 0}</td>
                                        <td className="p-2 border-r border-slate-100">{item.absent || 0}</td>
                                        <td className="p-2 bg-red-50 font-bold text-red-800">{item.totalNotPresent || 0}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {summary.length > 0 && (
                            <tfoot className="bg-slate-100 font-black text-slate-700 uppercase">
                                <tr>
                                    <td colSpan={2} className="p-3 border-r border-slate-200 text-right">{t('attendance.grandTotal')}</td>
                                    <td className="p-3 border-r border-slate-200 text-green-700 bg-green-100/50">{calculateTotal('totalStaff')}</td>
                                    <td className="p-3 border-r border-slate-200">{calculateTotal('onTime')}</td>
                                    <td className="p-3 border-r border-slate-200">{calculateTotal('lateMark')}</td>
                                    <td className="p-3 border-r border-slate-200">{calculateTotal('onDuty')}</td>
                                    <td
                                        className="p-3 border-r border-slate-200 text-blue-700 bg-blue-100/50 cursor-pointer underline hover:bg-blue-200 transition-colors"
                                        onClick={() => handleTotalClick('PRESENT')}
                                    >
                                        {calculateTotal('totalPresent')}
                                    </td>
                                    <td className="p-3 border-r border-slate-200">{calculateTotal('leave')}</td>
                                    <td className="p-3 border-r border-slate-200">{calculateTotal('absent')}</td>
                                    <td
                                        className="p-3 text-red-700 bg-red-100/50 cursor-pointer underline hover:bg-red-200 transition-colors"
                                        onClick={() => handleTotalClick('NOT_PRESENT')}
                                    >
                                        {calculateTotal('totalNotPresent')}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {/* Detail Table */}
                {(selectedDeptEmployees.length > 0 || detailLoading) && (
                    <div className="mt-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-8 w-1.5 bg-blue-600 rounded-full"></div>
                            <h5 className="font-black text-slate-700 uppercase text-base">{getDetailTitle()}</h5>
                            {detailLoading && <span className="text-[10px] text-blue-500 animate-pulse">Loading Details...</span>}
                        </div>
                        <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-md">
                            <table className="w-full text-left">
                                <thead className="bg-[#4fb9db] text-white uppercase tracking-wider">
                                    <tr>
                                        <th className="p-4 border-r border-cyan-300 text-center w-16">{t('tables.srNo')}</th>
                                        <th className="p-4 border-r border-cyan-300">{t('attendance.attendanceDate')}</th>
                                        <th className="p-4 border-r border-cyan-300">{t('attendance.employeeName')}</th>
                                        <th className="p-4 border-r border-cyan-300">{t('attendance.department')}</th>
                                        <th className="p-4 border-r border-cyan-300">{t('attendance.designation')}</th>
                                        <th className="p-4 text-center">{t('attendance.attendance')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {selectedDeptEmployees.map((emp, idx) => (
                                        <tr key={emp.employeeId || idx} className="hover:bg-cyan-50/30 transition-colors">
                                            <td className="p-3 border-r border-slate-50 text-center font-bold text-gray-400">{idx + 1}</td>
                                            <td className="p-3 border-r border-slate-50 text-slate-500">{date}</td>
                                            <td className="p-3 border-r border-slate-50 font-bold text-slate-700">{emp.employeeName || "N/A"}</td>
                                            <td className="p-3 border-r border-slate-50 text-slate-600">{emp.departmentName || "N/A"}</td>
                                            <td className="p-3 border-r border-slate-50 text-slate-600">{emp.designationName || "N/A"}</td>
                                            <td className="p-3 text-center">
                                                <span className={`px-3 py-1 rounded-full font-black text-sm uppercase ${emp.statusId === 1 ? "text-green-700" :
                                                    emp.statusId === 2 ? "text-blue-700" :
                                                        emp.statusId === 4 ? "text-red-700" : "text-gray-600"
                                                    }`}>
                                                    {emp.statusId === 1 ? "Present" :
                                                        emp.statusId === 2 ? "On Duty" :
                                                            emp.statusId === 3 ? "Leave" :
                                                                emp.statusId === 4 ? "Absent" :
                                                                    emp.statusId === 5 ? "Late Mark" : "Not Marked"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <MessageModal
                open={messageModal.open}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
            />
        </PageFade>
    )
}

export default DailyAttendance;

